import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DetailedListComponent, { DetailedListColumn } from '../../common/DetailedList/DetailedListComponent';
import MainScreenHeader from '../../common/Header/MainScreenHeader';
import ErrorMessage from '../../common/ErrorMessage/ErrorMessage';
import RoleGuard from '../../common/RoleGuard';
import { UserRoles } from '../../../constants/UserRoles';
import { LedgerEntryRestDataProvider } from '../../../data/LedgerEntryRestDataProvider';
import { LedgerEntry } from '../../../models/LedgerEntry';
import { LedgerRestDataProvider } from '../../../data/LedgerRestDataProvider';
import { Ledger } from '../../../models/Ledger';
import { LedgerType } from '../../../models/LedgerType';
import { FiscalYearRestDataProvider } from '../../../data/FiscalYearRestDataProvider';
import { FiscalYear } from '../../../models/FiscalYear';
import { FormFieldConfig } from '../../common/FormGenerator/FormGenerator';
import SelectInput from '../../widgets/SelectInput';
import { ToolbarFilterConfig } from '../../common/Toolbar/Toolbar';
import StatsBar from '../../common/StatsBar/StatsBar';
import './LedgerJournal.scss';
import '../../../styles/GenericSiteStyles.scss';

const LedgerJournal: React.FC = () => {
  const { t } = useTranslation(['common', 'screens']);
  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [ledgers, setLedgers] = useState<Ledger[]>([]);
  const [fiscalYears, setFiscalYears] = useState<FiscalYear[]>([]);
  const [selectedFiscalYearId, setSelectedFiscalYearId] = useState<string>('');
  const [selectedLedgerId, setSelectedLedgerId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  const entryProvider = useMemo(() => new LedgerEntryRestDataProvider(), []);
  const ledgerProvider = useMemo(() => new LedgerRestDataProvider(), []);
  const fiscalYearProvider = useMemo(() => new FiscalYearRestDataProvider(), []);

  // Load reference data on mount; auto-select the latest fiscal year
  useEffect(() => {
    Promise.all([ledgerProvider.getAll(), fiscalYearProvider.getAll()])
      .then(([fetchedLedgers, fetchedFiscalYears]) => {
        setLedgers(fetchedLedgers);
        const sorted = [...fetchedFiscalYears].sort((a, b) => b.start.localeCompare(a.start));
        setFiscalYears(sorted);
        if (sorted.length > 0) setSelectedFiscalYearId(sorted[0].id);
        setInitialized(true);
      })
      .catch(e => setError(e.message));
  }, [ledgerProvider, fiscalYearProvider]);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let all: LedgerEntry[];
      if (selectedLedgerId) {
        all = await entryProvider.getByLedgerId(selectedLedgerId);
      } else {
        all = await entryProvider.getAll();
      }
      const filtered = selectedFiscalYearId
        ? all.filter(e => e.fiscalYearId === selectedFiscalYearId)
        : all;
      const sortedByDate = [...filtered].sort((a, b) =>
        String(a.entryMoment ?? '').localeCompare(String(b.entryMoment ?? ''))
      );
      setEntries(sortedByDate);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [entryProvider, selectedFiscalYearId, selectedLedgerId]);

  // Reload entries whenever filters change (after initial reference data is ready)
  useEffect(() => {
    if (!initialized) return;
    loadEntries();
  }, [loadEntries, initialized]);

  const getLedger = useCallback((id: string) => ledgers.find(l => l.id === id), [ledgers]);

  const totalDebet = useMemo(() => entries.reduce((sum, e) => sum + Number(e.debetAmount ?? 0), 0), [entries]);
  const totalCredit = useMemo(() => entries.reduce((sum, e) => sum + Number(e.creditAmount ?? 0), 0), [entries]);
  const difference = totalDebet - totalCredit;

  /**
   * Cumulative running balance up to and including each row, keyed by entry id.
   *
   * When a single ledger account is selected the sign follows the *natural*
   * balance direction of that account type:
   *   - Debet / Costs    → debet − credit  (normal balance is a debit)
   *   - Credit / Proceeds → credit − debet  (normal balance is a credit)
   *
   * When all accounts are shown the raw (debet − credit) is used as a
   * double-entry journal balance check (should approach 0 for a balanced book).
   */
  const runningBalances = useMemo(() => {
    const map = new Map<string, number>();
    let running = 0;

    if (selectedLedgerId) {
      // Single-ledger view: respect the natural balance direction of the account type.
      const ledger = getLedger(selectedLedgerId);
      const isNaturalCredit =
        ledger?.type === LedgerType.Credit || ledger?.type === LedgerType.Proceeds;

      for (const e of entries) {
        running += isNaturalCredit
          ? Number(e.creditAmount ?? 0) - Number(e.debetAmount ?? 0)
          : Number(e.debetAmount ?? 0) - Number(e.creditAmount ?? 0);
        map.set(e.id, running);
      }
    } else {
      // All-accounts view: raw debet − credit (double-entry balance check).
      for (const e of entries) {
        running += Number(e.debetAmount ?? 0) - Number(e.creditAmount ?? 0);
        map.set(e.id, running);
      }
    }

    return map;
  }, [entries, selectedLedgerId, getLedger]);

  const columns: DetailedListColumn<LedgerEntry>[] = useMemo(() => [
    {
      key: 'entryMoment',
      header: t('screens:ledgerJournal.columns.date'),
      dataField: 'entryMoment',
      type: 'date',
      required: true,
      width: 120,
    },
    {
      key: 'fiscalYearId',
      header: t('screens:ledgerJournal.columns.fiscalYear'),
      width: 140,
      render: e => fiscalYears.find(fy => fy.id === e.fiscalYearId)?.name ?? '',
    },
    {
      key: 'ledgerCode',
      header: t('screens:ledgerJournal.columns.accountCode'),
      render: e => getLedger(e.ledgerId)?.code ?? '',
      width: 140,
    },
    {
      key: 'ledgerName',
      header: t('screens:ledgerJournal.columns.accountName'),
      render: e => getLedger(e.ledgerId)?.name ?? '',
      width: 200,
    },
    {
      key: 'bookingReference',
      header: t('screens:ledgerJournal.columns.reference'),
      dataField: 'bookingReference',
      type: 'string',
      width: 160,
    },
    {
      key: 'description',
      header: t('screens:ledgerJournal.columns.description'),
      dataField: 'description',
      type: 'string',
    },
    {
      key: 'debetAmount',
      header: t('screens:ledgerJournal.columns.debet'),
      dataField: 'debetAmount',
      type: 'number',
      align: 'right',
      width: 130,
      render: e => e.debetAmount ? `€ ${Number(e.debetAmount).toFixed(2)}` : '',
    },
    {
      key: 'creditAmount',
      header: t('screens:ledgerJournal.columns.credit'),
      dataField: 'creditAmount',
      type: 'number',
      align: 'right',
      width: 130,
      render: e => e.creditAmount ? `€ ${Number(e.creditAmount).toFixed(2)}` : '',
    },
    {
      key: 'runningBalance',
      header: t('screens:ledgerJournal.columns.runningBalance'),
      align: 'right',
      width: 150,
      render: e => {
        const bal = runningBalances.get(e.id) ?? 0;
        const balanced = Math.abs(bal) < 0.005;
        return (
          <span className={`lj-running-balance ${balanced ? 'lj-running-balance--ok' : 'lj-running-balance--off'}`}>
            {balanced ? '✓' : '⚠'}&nbsp;€&nbsp;{bal.toFixed(2)}
          </span>
        );
      },
    },
  ], [getLedger, runningBalances, fiscalYears, t]);

  const formFields: FormFieldConfig[] = useMemo(() => [
    { key: 'entryMoment', label: t('screens:ledgerJournal.formFields.date'), type: 'date', required: true },
    {
      key: 'fiscalYearId',
      label: t('screens:ledgerJournal.formFields.fiscalYear'),
      type: 'select',
      required: true,
      options: fiscalYears.map(fy => ({ label: fy.name, value: fy.id })),
      defaultValue: selectedFiscalYearId || undefined,
    },
    {
      key: 'ledgerId',
      label: t('screens:ledgerJournal.formFields.ledgerAccount'),
      type: 'select',
      required: true,
      options: ledgers.map(l => ({ label: `${l.code} - ${l.name}`, value: l.id })),
      defaultValue: selectedLedgerId || undefined,
    },
    { key: 'bookingReference', label: t('screens:ledgerJournal.formFields.bookingReference'), type: 'string' },
    { key: 'description', label: t('screens:ledgerJournal.formFields.description'), type: 'multiline', rows: 2 },
    { key: 'creditAmount', label: t('screens:ledgerJournal.formFields.creditAmount'), type: 'number' },
    { key: 'debetAmount', label: t('screens:ledgerJournal.formFields.debetAmount'), type: 'number' },
  ], [ledgers, fiscalYears, selectedLedgerId, selectedFiscalYearId, t]);

  const handleChange = async (
    _newItems: LedgerEntry[],
    action: 'add' | 'edit' | 'delete',
    changedItem: LedgerEntry
  ) => {
    try {
      if (action === 'add') {
        const entry: LedgerEntry = {
          ...changedItem,
          id: crypto.randomUUID(),
          // Prefer the fiscal year chosen in the form; fall back to the toolbar filter.
          fiscalYearId: changedItem.fiscalYearId || selectedFiscalYearId,
          createdAt: new Date().toISOString(),
          modifiedAt: new Date().toISOString(),
        };
        await entryProvider.add(entry);
      } else if (action === 'edit') {
        await entryProvider.update(changedItem.id, {
          ...changedItem,
          modifiedAt: new Date().toISOString(),
        });
      } else if (action === 'delete') {
        await entryProvider.delete(changedItem.id);
      }
      await loadEntries();
    } catch (e: any) {
      setError(e.message);
    }
  };

  // ── Toolbar configs ──────────────────────────────────────────────────────────
  const toolbarFilters = useMemo<ToolbarFilterConfig[]>(() => [
    {
      key: 'fiscalYear',
      type: 'select',
      label: t('common:fiscalYear'),
      value: selectedFiscalYearId,
      onChange: setSelectedFiscalYearId,
      allLabel: t('common:allFiscalYears'),
      options: fiscalYears.map(fy => ({ label: fy.name, value: fy.id })),
    },
    {
      key: 'ledgerAccount',
      type: 'custom',
      render: () => (
        <SelectInput
          label={t('screens:ledgerJournal.ledgerAccount')}
          value={selectedLedgerId}
          options={[
            { label: t('screens:ledgerJournal.allAccounts'), value: '' },
            ...ledgers.map(l => ({ label: `${l.code} – ${l.name}`, value: l.id })),
          ]}
          onChange={setSelectedLedgerId}
        />
      ),
    },
  ], [fiscalYears, selectedFiscalYearId, ledgers, selectedLedgerId, t]);

  return (
    <RoleGuard roles={`${UserRoles.Admin},${UserRoles.Manager},${UserRoles.User}`} redirectOnDeny>
      <div className="ledger-journal-page">
        <MainScreenHeader title={t('screens:ledgerJournal.title')} />
        {error && <ErrorMessage message={error} />}

        {/* ── Summary stats strip ── */}
        <StatsBar flush items={[
          { key: 'debet',      label: t('screens:ledgerJournal.debet'),      value: `€ ${totalDebet.toFixed(2)}` },
          { key: 'credit',     label: t('screens:ledgerJournal.credit'),     value: `€ ${totalCredit.toFixed(2)}` },
          { key: 'difference', label: t('screens:ledgerJournal.difference'), value: `€ ${difference.toFixed(2)}`, variant: difference === 0 ? 'positive' : 'negative' },
        ]} />

        <DetailedListComponent
          dataSource={{ data: entries, setData: setEntries }}
          columns={columns}
          formFields={formFields}
          rowKey={e => e.id}
          onChange={handleChange}
          editRole={`${UserRoles.Manager},${UserRoles.Admin}`}
          addRole={`${UserRoles.Manager},${UserRoles.Admin}`}
          deleteRole={`${UserRoles.Manager},${UserRoles.Admin}`}
          toolbarFilters={toolbarFilters}
        />
        {loading && <div className="ledger-journal-loading">{t('common:loading')}</div>}
      </div>
    </RoleGuard>
  );
};

export default LedgerJournal;
